import React, { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  LinearProgress,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { useNavigate, useParams } from 'react-router-dom'
import {
  usePurchaseInvoice,
  useRecordVendorPayment,
} from '../../hooks/api/usePurchaseInvoices'
import PaymentAccountPicker from '../../components/ui/PaymentAccountPicker'
import { toPaisa, fromPaisa } from '../../utils/money'

const fmt = (n: number) =>
  n.toLocaleString('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 2 })

const paymentLabel = (status: string) =>
  status === 'paid' ? 'Paid' : status === 'partial' ? 'Partial' : 'Unpaid'
const paymentVariant = (status: string) =>
  status === 'paid' ? 'success' as const : status === 'partial' ? 'warn' as const : 'danger' as const

const PurchaseInvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split('T')[0])
  const [paymentRemarks, setPaymentRemarks] = useState('')
  const [paymentSource, setPaymentSource] = useState('cash')

  const { data: invoice, isLoading, isError } = usePurchaseInvoice(id!)
  const paymentMutation = useRecordVendorPayment(id!)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !invoice) {
    return <Alert severity="error">Failed to load purchase invoice. Please try again.</Alert>
  }

  const handleRecordPayment = async () => {
    if (!/^\d+(\.\d{1,2})?$/.test(paymentAmount)) return
    const amount = fromPaisa(toPaisa(Number(paymentAmount)))
    if (amount <= 0 || toPaisa(amount) > remainingPaisa) return
    await paymentMutation.mutateAsync({
      amount,
      payment_date: paymentDate,
      remarks: paymentRemarks,
      payment_account: paymentSource === 'cash' ? 'cash' : 'bank',
      ...(paymentSource !== 'cash' && { bank_account_id: paymentSource }),
    })
    setPaymentDialogOpen(false)
    setPaymentAmount('')
    setPaymentRemarks('')
    setPaymentSource('cash')
  }

  const skuLabel = (sku_id: any) => {
    if (!sku_id) return '—'
    if (typeof sku_id === 'object') return sku_id.name
    return sku_id
  }

  const total = invoice.summary.net_value_before_tax
  const totalPaisa = toPaisa(total)
  const paidPaisa = toPaisa(invoice.amount_paid)
  const remainingPaisa = Math.max(0, totalPaisa - paidPaisa)
  const remaining = fromPaisa(remainingPaisa)
  const paidPct = totalPaisa > 0 ? Math.min((paidPaisa / totalPaisa) * 100, 100) : 0
  const canPay = invoice.payment_status !== 'paid' && remainingPaisa > 0

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/purchase-invoices')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Purchase Invoice
          </Typography>
          <StatusChip
            label={paymentLabel(invoice.payment_status)}
            variant={paymentVariant(invoice.payment_status)}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canPay && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PaymentIcon />}
              onClick={() => setPaymentDialogOpen(true)}
            >
              Record Payment
            </Button>
          )}
        </Box>
      </Box>

      {/* Invoice Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="overline" color="text.secondary">Vendor</Typography>
            <Typography variant="h6" fontWeight={600}>{invoice.vendor.name}</Typography>
            {invoice.vendor.address && (
              <Typography variant="body2">{invoice.vendor.address}</Typography>
            )}
            {invoice.vendor.ntn && (
              <Typography variant="body2">NTN: {invoice.vendor.ntn}</Typography>
            )}
            {invoice.vendor.strn && (
              <Typography variant="body2">STRN: {invoice.vendor.strn}</Typography>
            )}
            {invoice.vendor.phone && (
              <Typography variant="body2">Phone: {invoice.vendor.phone}</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="overline" color="text.secondary">Invoice Details</Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Invoice Date</Typography>
                <Typography>{format(new Date(invoice.invoice_date), 'dd MMM yyyy')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">
                  {format(new Date(invoice.createdAt), 'dd MMM yyyy, HH:mm')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Line Items */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Line Items
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell align="right">Qty CTN</TableCell>
                <TableCell align="right">Qty PCS</TableCell>
                <TableCell align="right">Rate / CTN</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.line_items.map((item, idx) => (
                <TableRow key={item._id ?? idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{skuLabel(item.sku_id)}</TableCell>
                  <TableCell align="right">{item.quantity_ctn}</TableCell>
                  <TableCell align="right">{item.quantity_pcs}</TableCell>
                  <TableCell align="right">{item.rate.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <strong>{item.net_value_before_tax.toLocaleString()}</strong>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Summary */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ maxWidth: 340, ml: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700}>Grand Total</Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {fmt(total)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Payment Summary */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Payment
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Paid {fmt(invoice.amount_paid)} of {fmt(total)}
            </Typography>
            <StatusChip label={paymentLabel(invoice.payment_status)} variant={paymentVariant(invoice.payment_status)} />
          </Box>
          <LinearProgress
            variant="determinate"
            value={paidPct}
            color={invoice.payment_status === 'paid' ? 'success' : invoice.payment_status === 'partial' ? 'warning' : 'error'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          {remaining > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Remaining: {fmt(remaining)}
            </Typography>
          )}
        </Box>

        {invoice.payments.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              Payment History
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.payments.map((p) => (
                  <TableRow key={p._id}>
                    <TableCell>{format(new Date(p.payment_date), 'dd MMM yyyy')}</TableCell>
                    <TableCell align="right">{fmt(p.amount)}</TableCell>
                    <TableCell>{p.remarks || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>

      {/* Record Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {(() => {
            const validAmount = /^\d+(\.\d{1,2})?$/.test(paymentAmount)
            const isExcess = !!paymentAmount && validAmount && toPaisa(Number(paymentAmount)) > remainingPaisa
            return (
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Remaining: <strong>{fmt(remaining)}</strong>
                </Typography>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <TextField
                    label="Amount *"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
                    }}
                    inputProps={{ min: 0.01, step: 0.01 }}
                    fullWidth
                    size="small"
                    autoFocus
                    error={isExcess || (!!paymentAmount && !validAmount)}
                    helperText={isExcess ? `Cannot exceed remaining balance of ${fmt(remaining)}`
                      : paymentAmount && !validAmount ? 'Enter an amount with up to two decimal places' : ''}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPaymentAmount(fromPaisa(remainingPaisa).toFixed(2))}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0, mt: 0.25 }}
                  >
                    Full Amount
                  </Button>
                </Box>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Remarks (optional)"
                  value={paymentRemarks}
                  onChange={(e) => setPaymentRemarks(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
                <PaymentAccountPicker value={paymentSource} onChange={setPaymentSource} label="Paid from" />
              </Box>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleRecordPayment}
            disabled={
              !paymentAmount ||
              !/^\d+(\.\d{1,2})?$/.test(paymentAmount) ||
              toPaisa(Number(paymentAmount)) <= 0 ||
              toPaisa(Number(paymentAmount)) > remainingPaisa ||
              paymentMutation.isPending
            }
            startIcon={paymentMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {paymentMutation.isPending ? 'Saving...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PurchaseInvoiceDetailPage
